"use client";

import { useState, type ReactNode } from "react";
import "@/styles/shared/_accordion.scss";

export interface AccordionItem {
  id: string | number;
  title: string;
  content: string | ReactNode;
}

interface AccordionProps {
  items: AccordionItem[];
  variant?: "shared" | "faq";
  activeColor?: string;
  activeTextColor?: string;
  headerColor?: string;
  headerBg?: string;
  hoverBg?: string;
  className?: string;
}

export default function Accordion({
  items,
  variant = "shared",
  activeColor,
  activeTextColor,
  headerColor,
  headerBg,
  hoverBg,
  className = "",
}: AccordionProps) {
  const [activeId, setActiveId] = useState<string | number>(items[0]?.id ?? "");

  const handleToggle = (itemId: string | number) => {
    setActiveId((prev) => (prev === itemId ? "" : itemId));
  };

  const styleProps =
    variant === "shared"
      ? ({
          "--accordion-active-color": activeColor,
          "--accordion-active-text-color": activeTextColor,
          "--accordion-header-color": headerColor,
          "--accordion-header-bg": headerBg,
          "--accordion-hover-bg": hoverBg,
        } as React.CSSProperties)
      : undefined;

  return (
    <div
      className={`${variant === "shared" ? "shared-accordion" : "faq-list"} ${className}`}
      role={variant === "shared" ? "tablist" : "list"}
      style={styleProps}
    >
      {items.map((item) => {
        const isActive = item.id === activeId;
        return (
          variant === "shared" ? (
            <div
              key={item.id}
              className={`shared-accordion__item ${isActive ? "shared-accordion__item--active" : ""}`}
            >
              <button
                type="button"
                className="shared-accordion__header"
                id={`accordion-${item.id}-header`}
                aria-expanded={isActive}
                aria-controls={`accordion-${item.id}-content`}
                onClick={() => handleToggle(item.id)}
              >
                {item.title}
              </button>
              <div
                className="shared-accordion__content"
                id={`accordion-${item.id}-content`}
                role="region"
                aria-labelledby={`accordion-${item.id}-header`}
                hidden={!isActive}
              >
                {typeof item.content === "string" ? <p>{item.content}</p> : item.content}
              </div>
            </div>
          ) : (
            <div
              key={item.id}
              className={`faq-item ${isActive ? "faq-item--active" : ""}`}
              role="listitem"
            >
              <button
                type="button"
                className="faq-item__header"
                id={`faq-${item.id}-header`}
                aria-expanded={isActive}
                aria-controls={`faq-${item.id}-content`}
                onClick={() => handleToggle(item.id)}
              >
                <h3 className="faq-item__title">{item.title}</h3>
                <span className="faq-item__icon" aria-hidden="true"></span>
              </button>
              <div
                className="faq-item__content"
                id={`faq-${item.id}-content`}
                role="region"
                aria-labelledby={`faq-${item.id}-header`}
                aria-hidden={!isActive}
              >
                {typeof item.content === "string" ? (
                  <p className="faq-item__description">{item.content}</p>
                ) : (
                  item.content
                )}
              </div>
            </div>
          )
        );
      })}
    </div>
  );
}

